/**
 * Earnings zoals een CHATTER hem hoort te zien: zijn eigen cijfers.
 *
 * 10-09-2026 (Jordy): ingelogd als chatter stond elke kaart op nul en zat er
 * een rood blok tussen met "Manager access required". De gewone
 * Earnings-pagina haalt alles uit `/api/admin/dashboard/...`, en die routes
 * zijn stuk voor stuk manager-gated. Een chatter kreeg dus zeven 403's.
 *
 * Dit scherm is de nieuwe stijl bovenop EXACT hetzelfde als het oude scherm
 * "My Earnings — <naam>". Uit de uitgerolde bundel van oud gelezen, niet
 * geraden: dezelfde drie routes, dezelfde blokken, dezelfde kolommen.
 *
 *   GET  /chatter-finance/earnings/{id}?days=      kaarten + My Sales
 *   GET  /chatter-finance/payouts?chatter_id=      Payout history
 *   GET  /chatter-finance/manual-earnings?...      handmatige regels
 *   POST /chatter-finance/manual-earnings          zelf een regel indienen
 *   GET  /chatter-finance/team-sales?days=         Team sales (alleen manager)
 *
 * Alle vier zijn op de SERVER afgeschermd op de eigen chatter: je eigen rij
 * geeft 200, die van een collega 403. Het scherm verbergt niets wat de server
 * zou prijsgeven.
 *
 * LET OP bij het wijzigen: alle haken staan boven elke `return`. Stond er een
 * `return` tussen, dan telt React bij de tweede render minder haken en gooit
 * hij de HELE boom weg — een zwart scherm, geen foutmelding. Dat gebeurde op
 * 10-09; zie de uitleg bij `Earnings`.
 */
import { useMemo, useState } from 'react'
import { Panel, Empty, Tag, Seg } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'
import { isManager, useMij } from '../lib/rechten'
import { useMobiel } from '../lib/mobiel'

/** Eén verkoop, met de veldnamen zoals de server ze levert. */
interface Verkoop {
  revenue_event_id: number
  occurred_at: string | null
  event_type: string | null
  model_name: string | null
  fan: string | null
  platform: string | null
  amount: number | null
  net: number | null
  conversation_id: number | null
  message_id: number | null
  /** EARN-004B: telt deze verkoop mee voor commissie? */
  counts_for_commission?: boolean
}

interface Handmatig {
  id: number
  amount: number | null
  occurred_at: string | null
  note: string | null
  status: string | null
}

interface Uitbetaling {
  id: number
  period_start: string | null
  period_end: string | null
  gross_amount: number | null
  commission_amount: number | null
  amount_paid: number | null
  status: string | null
  paid_at: string | null
  note: string | null
}

/** De openstaande stand, zoals de SERVER hem rekent. Niet zelf afleiden. */
interface Balans {
  chatter_id: number
  open_commission: number | null
  open_own_commission: number | null
  open_referral_commission: number | null
  paid_commission: number | null
  earned_commission_alltime: number | null
}

interface EigenVerdiensten {
  chatter_id: number
  chatter_name: string
  commission_pct: number
  is_lead: boolean
  gross_platform: number
  gross_manual: number
  gross_total: number
  own_commission: number
  referral_earned: number
  commission_amount: number
  platform_sales: Verkoop[]
  manual_earnings: Handmatig[]
  platform_sale_count: number
  manual_earning_count: number
  /** EARN-004B: de overgangsdag, of null zolang het oude dashboard uitbetaalt. */
  commission_since?: string | null
  commission_notice?: string | null
}

/** Eén verkoop in Team sales. De server geeft alleen deze velden: geen
 *  commissie, netto, uitbetaling of commissiepercentage van wie dan ook.
 *  `referral` is alleen het EIGEN referral-bedrag (en head-%) van de manager,
 *  of een reden zonder bedrag. 31-10-2026 (Jordy, D1): `group` voor het filter. */
interface TeamVerkoop {
  revenue_event_id: number
  occurred_at: string | null
  chatter_name: string | null
  event_type: string | null
  model_name: string | null
  fan: string | null
  platform: string | null
  gross: number | null
  referral?: { pct?: number; amount?: number; reason?: string } | null
  group?: TeamGroep
}

type TeamGroep = 'yours' | 'my_team' | 'other_teams' | 'unassigned'
type TeamFilter = 'all' | TeamGroep
interface TeamTotaal { count: number; gross: number; referral: number }

/** 31-10-2026 (Jordy, D1): het filter boven Team sales. */
const TEAM_FILTERS: { value: TeamFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'yours', label: 'Yours' },
  { value: 'my_team', label: 'My team' },
  { value: 'other_teams', label: 'Other teams' },
  // 2-11-2026 (Jordy): verkopen zonder toegewezen chatter, alleen bruto.
  { value: 'unassigned', label: 'Unassigned' },
]

/**
 * 31-10-2026 (Jordy, D1): de Referral-kolom als korte badge; de uitleg (de
 * redenen van 30-10: before link, not counted, ...) staat in de tooltip, op
 * een telefoon eronder. Groen alleen als de verkoop jou referral opleverde.
 * Het percentage is het EIGEN head-% van de manager, nooit de commissie van
 * de chatter.
 */
function referralBadge(v: TeamVerkoop): { tekst: string; kind: 'ok' | 'mut'; uitleg: string } {
  const r = v.referral
  const wie = v.chatter_name || 'This chatter'
  if (v.group === 'unassigned' || r?.reason === 'unassigned') {
    return { tekst: 'Unassigned', kind: 'mut',
      uitleg: 'No chatter is assigned to this sale (for example a tip without an earlier message from a chatter), so it earns no one referral. Gross only.' }
  }
  if (v.group === 'yours' || r?.reason === 'own_sale') {
    return { tekst: 'Own sale', kind: 'mut',
      uitleg: 'Your own sale. Your commission on it is under My Sales; your own sales earn no referral.' }
  }
  if (r && r.amount != null && r.pct != null) {
    return { tekst: `Your team · ${r.pct}% · ${geld(r.amount)}`, kind: 'ok',
      uitleg: `${wie} was on your team when this sale was made: your referral is ${r.pct}% = ${geld(r.amount)}.` }
  }
  const reden = String(r?.reason ?? '')
  const waarom = reden === 'before_link'
    ? `Made before ${wie} joined your team, so it earns you no referral.`
    : reden === 'not_counted'
      ? 'Made before the commission start date, so it does not count for referral.'
      : reden === 'no_pct'
        ? 'No referral % was set for you when this sale was made.'
        : v.group === 'my_team'
          ? `${wie} was not on your team when this sale was made, so it earns you no referral.`
          : `${wie} is not on your team, so this sale earns you no referral.`
  return v.group === 'my_team'
    ? { tekst: 'Your team · no referral', kind: 'mut', uitleg: waarom }
    : { tekst: 'Other team · no referral', kind: 'mut', uitleg: waarom }
}

/** De badge zelf: nooit afgekapt (geen ellipsis, geen overflow), ook niet waar
 *  een tabelcel elders wel afkapt (theme.css onder 1100px). */
const BADGE_STIJL: React.CSSProperties = {
  whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip', maxWidth: 'none',
}

/** De vensters van oud, in dezelfde volgorde. "Total" is alles. */
const VENSTERS: { waarde: string; label: string }[] = [
  { waarde: '3650', label: 'Total' },
  { waarde: '1', label: 'Today' },
  { waarde: '7', label: '7d' },
  { waarde: '30', label: '30d' },
  { waarde: '90', label: '90d' },
  { waarde: '365', label: '1y' },
]

/**
 * Een tijd van de server staat in UTC; een chatter leest Amsterdamse tijd.
 *
 * Jordy zag 19:42 staan waar 21:42 hoort. De server geeft een kale tijd zonder
 * zone, dus die moet hier expliciet als UTC gelezen worden — anders leest de
 * browser hem als lokale tijd en verschuift er niets.
 */
function tijd(ruw: string | null | undefined): string {
  if (!ruw) return '—'
  const tekst = /(\+|-)\d{2}:\d{2}$|Z$/.test(ruw) ? ruw : `${ruw}Z`
  const d = new Date(tekst)
  if (Number.isNaN(d.getTime())) return String(ruw).slice(0, 16).replace('T', ' ')
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/Amsterdam',
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).replace(',', '')
}

function datum(ruw: string | null | undefined): string {
  if (!ruw) return '—'
  return tijd(ruw).split(' ')[0]
}

const geld = (v: unknown): string => `€${(Number(v ?? 0) || 0).toFixed(2)}`

/** "chat_attachment" -> "Attachment unlock", zoals oud het schrijft. */
const SOORT: Record<string, string> = {
  chat_attachment: 'Attachment unlock',
  paid_message: 'Paid message',
  dm_unlock: 'Paid message',
  tip: 'Tip',
  subscription: 'Subscription',
  chat_message: 'Paid message',
}
const soort = (t: string | null): string =>
  SOORT[String(t ?? '')] ?? String(t ?? '—').replace(/_/g, ' ')

function Kaart({ label, waarde, onder, nadruk, testid }: {
  label: string; waarde: string; onder?: string; nadruk?: boolean; testid?: string
}) {
  return (
    <div data-testid={testid} style={nadruk
      ? { minWidth: 165, padding: '6px 12px', borderRadius: 8,
          border: '1px solid var(--acc)', background: 'var(--panel2)' }
      : { minWidth: 165 }}>
      <div className="mut2" style={{ fontSize: 12 }}>{label}</div>
      <div className="num mono" style={{ fontSize: 22, fontWeight: 600 }}>{waarde}</div>
      {onder ? <div className="mut2" style={{ fontSize: 11 }}>{onder}</div> : null}
    </div>
  )
}

export function ChatterEarnings({ chatterId }: { chatterId: number | null }) {
  const [days, setDays] = useState('30')
  const [van, setVan] = useState('')
  const [tot, setTot] = useState('')
  const [bedrag, setBedrag] = useState('')
  const [wanneer, setWanneer] = useState('')
  const [notitie, setNotitie] = useState('')
  const [bewijs, setBewijs] = useState<File | null>(null)
  const [bezig, setBezig] = useState(false)
  const [melding, setMelding] = useState('')
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('all')
  const mobiel = useMobiel()

  const venster = van && tot ? `from_date=${van}&to_date=${tot}` : `days=${days}`
  const basis = chatterId ? `/api/admin/chatter-finance` : ''

  // Alle haken staan hier, boven elke `return`. Een lege url laat `useApi`
  // niets ophalen, dus een account zonder gekoppelde chatter kost geen verzoek.
  const eigen = useApi<EigenVerdiensten>(
    basis ? `${basis}/earnings/${chatterId}?${venster}` : '', [chatterId, venster])
  const uitbetalingen = useApi<{ items: Uitbetaling[] }>(
    basis ? `${basis}/payouts?chatter_id=${chatterId}` : '', [chatterId])
  const handmatig = useApi<{ items: Handmatig[] }>(
    basis ? `${basis}/manual-earnings?chatter_id=${chatterId}&${venster}` : '',
    [chatterId, venster])
  // De openstaande stand is GEEN venstercijfer: het is alles wat je ooit
  // verdiende min alles wat al is uitbetaald. De server rekent dat en scherpt
  // hem af op je eigen rij — een chatter krijgt alleen zichzelf terug.
  const balansen = useApi<{ per_chatter: Balans[] }>(
    basis ? `${basis}/sales-per-chatter-balances` : '', [chatterId])
  // 29-10-2026 (Jordy): Team sales, alleen voor een manager. De server geeft
  // een ander dan een manager een 403; hier vragen we het dan ook niet.
  const mij = useMij()
  const manager = isManager(mij.data)
  const team = useApi<{ items: TeamVerkoop[]; totals?: Record<TeamFilter, TeamTotaal> }>(
    basis && manager ? `${basis}/team-sales?${venster}` : '', [chatterId, venster, manager])
  // 31-10-2026 (Jordy, D1): het filter. De totalen komen van de server en zijn
  // daar de som van precies deze rijen (team_sales_totalen); "Yours" is My
  // Sales zonder abonnementen, dus er staat nergens een som met My Sales.
  const teamRijen = useMemo(
    () => (team.data?.items ?? []).filter((v) => teamFilter === 'all' || v.group === teamFilter),
    [team.data, teamFilter])
  const teamTotaal = team.data?.totals?.[teamFilter] ?? null

  // Een chatter wil zijn verkoop zien zodra hij binnenkomt, niet na een
  // schermverversing. De luisteraar schrijft een verkoop binnen seconden weg
  // (gemeten 10-09: fan betaalt, rij staat er, toegewezen aan de juiste
  // chatter), dus het enige wat ontbrak was dit scherm dat opnieuw kijkt.
  //
  // Vijftien seconden. Het chatscherm ververst elke 2,5 s omdat daar een
  // gesprek loopt; verdiensten veranderen hooguit een paar keer per uur, en
  // vier verzoeken per minuut per ingelogde chatter is genoeg zonder de
  // database bezig te houden.
  usePoll(15000, eigen.refetch)
  usePoll(15000, balansen.refetch)
  usePoll(60000, uitbetalingen.refetch)
  usePoll(60000, handmatig.refetch)
  usePoll(60000, team.refetch)

  const betaald = useMemo(
    () => (uitbetalingen.data?.items ?? [])
      .filter((u) => String(u.status ?? '').toLowerCase() === 'paid')
      .reduce((s, u) => s + Number(u.amount_paid ?? 0), 0),
    [uitbetalingen.data])

  /**
   * De eigen balansrij.
   *
   * 11-09-2026 (Jordy). Hier stond `commission_amount - betaald`: het
   * venstercijfer min alles wat ooit is uitbetaald. Voor Max gaf dat
   * 6,61 − 41,29 = negatief, en een `Math.max(…, 0)` maakte daar € 0,00 van,
   * terwijl de eigenaar op zijn scherm € 1,84 zag staan. Twee schermen, twee
   * getallen, en het verkeerde was het mijne.
   *
   * Open staat los van het gekozen venster. De server rekent het al; dit
   * scherm neemt het over in plaats van er zelf iets van te maken.
   */
  const balans = useMemo(
    () => (balansen.data?.per_chatter ?? []).find(
      (b) => Number(b.chatter_id) === Number(chatterId)) ?? null,
    [balansen.data, chatterId])

  async function indienen(e: React.FormEvent) {
    e.preventDefault()
    if (!chatterId || !bedrag) return
    setBezig(true); setMelding('')
    try {
      const form = new FormData()
      form.append('chatter_id', String(chatterId))
      form.append('amount', bedrag)
      if (wanneer) form.append('occurred_at', wanneer)
      if (notitie) form.append('note', notitie)
      if (bewijs) form.append('proof', bewijs)
      const token = localStorage.getItem('admin_access_token') || ''
      const r = await fetch('/api/admin/chatter-finance/manual-earnings', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      })
      if (r.ok) {
        const j = await r.json().catch(() => ({}))
        setMelding(`Submitted — status: ${j.status ?? 'pending review'}`)
        setBedrag(''); setNotitie(''); setBewijs(null)
        handmatig.refetch?.(); eigen.refetch?.()
      } else {
        const f = await r.json().catch(() => ({}))
        setMelding(`Error: ${f.detail ?? r.status}`)
      }
    } catch (fout) {
      setMelding(`Error: ${String(fout).slice(0, 120)}`)
    }
    setBezig(false)
  }

  if (!chatterId) {
    return (
      <Panel title="My Earnings">
        <Empty>Your account is not linked to a chatter, so there are no
          earnings to show. Ask an administrator to link it.</Empty>
      </Panel>
    )
  }

  const d = eigen.data

  return (
    <>
      <Panel inklap="mijn-earnings.overzicht"
        title={`My Earnings${d?.chatter_name ? ` — ${d.chatter_name}` : ''}`}
        desc="Your own sales and commission"
        right={
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {VENSTERS.map((v) => (
              <button
                key={v.waarde}
                className={`btn sm${days === v.waarde && !van && !tot ? '' : ' ghost'}`}
                onClick={() => { setDays(v.waarde); setVan(''); setTot('') }}
              >{v.label}</button>
            ))}
            <input className="inp" type="date" value={van} title="From"
                   onChange={(e) => setVan(e.target.value)} style={{ width: 138 }} />
            <input className="inp" type="date" value={tot} title="To"
                   onChange={(e) => setTot(e.target.value)} style={{ width: 138 }} />
          </div>
        }
      >
        {/* EARN-004B, besluit Jordy 14-09-2026: tot de overgangsdag loopt de
            uitbetaling via het oude dashboard. De verkopen staan hier wel,
            maar tellen nog niet mee voor commissie. */}
        {d?.commission_notice ? (
          <div className="mut2" role="note"
               style={{ margin: '2px 0 10px', padding: '8px 12px', borderRadius: 8,
                        border: '1px solid rgba(245,158,11,0.4)',
                        background: 'rgba(245,158,11,0.10)', fontSize: 12.5 }}>
            {d.commission_notice}
          </div>
        ) : null}
        {!d ? <Empty>Loading…</Empty> : (
          <>
          {/* 30-10-2026 (Jordy): bovenaan, op één rij, de som zelf: Total earned
              = Own commission + Referral earned. Referral earned is een eigen,
              opvallende kaart (het referral-deel uit de head-chatterlink).
              Zelfde venster; de server rekent alle drie (commission_amount). */}
          <div data-testid="verdienst-som"
               style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', padding: '4px 0' }}>
            <Kaart label="Total earned" waarde={geld(d.commission_amount)} />
            <span className="mut2" style={{ fontSize: 22 }} aria-hidden>=</span>
            <Kaart label={`Own commission (${d.commission_pct}%)`}
                   waarde={geld(d.own_commission)} />
            {d.is_lead || Number(d.referral_earned) ? (
              <>
                <span className="mut2" style={{ fontSize: 22 }} aria-hidden>+</span>
                <Kaart label="Referral earned" waarde={geld(d.referral_earned)} nadruk
                       testid="referral-earned"
                       onder="From the chatters who report to you" />
              </>
            ) : null}
          </div>
          {d.is_lead || Number(d.referral_earned) ? (
            <div className="mut2" style={{ fontSize: 12, margin: '2px 0 6px' }}>
              Total earned = Own commission + Referral earned
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap', padding: '4px 0' }}>
            <Kaart label="Gross (platform)" waarde={geld(d.gross_platform)}
                   onder={`${d.platform_sale_count} sales`} />
            <Kaart label="Gross (manual)" waarde={geld(d.gross_manual)}
                   onder={`${d.manual_earning_count} entries (approved)`} />
            <Kaart label="Gross total" waarde={geld(d.gross_total)} />
          </div>
          </>
        )}
      </Panel>

      <Panel inklap="mijn-earnings.open-saldo" title="Open balance"
             desc="Commission earned since the last unpaid payout.">
        <div style={{ padding: '2px 0' }}>
          <div className="mut2" style={{ fontSize: 12 }}>Open / still to pay</div>
          <div className="num mono"
               style={{
                 fontSize: 30,
                 fontWeight: 700,
                 color: Number(balans?.open_commission ?? 0) > 0.005
                   ? 'var(--warn)' : 'var(--ok)',
               }}>
            {balansen.loading && !balans ? '—' : geld(balans?.open_commission)}
          </div>
          <div className="mut2" style={{ fontSize: 12 }}>
            Own commission {geld(balans?.open_own_commission)} ·
            Referral {geld(balans?.open_referral_commission)}
          </div>
          <div className="mut2" style={{ fontSize: 11, marginTop: 4 }}>
            All time earned {geld(balans?.earned_commission_alltime)} ·
            paid out {geld(balans?.paid_commission)}. This total does not
            follow the period buttons above.
          </div>
        </div>
      </Panel>

      <Panel inklap="mijn-earnings.handmatig" title="Submit manual earning"
             desc="For off-platform sales (cash tips, missed attributions, etc.). Upload a screenshot/receipt; admin will review.">
        <form onSubmit={indienen}
              style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span className="mut2" style={{ fontSize: 12 }}>Amount (EUR)</span>
            <input className="inp" type="number" step="0.01" min="0" required
                   value={bedrag} onChange={(e) => setBedrag(e.target.value)}
                   style={{ width: 130 }} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span className="mut2" style={{ fontSize: 12 }}>Occurred at</span>
            <input className="inp" type="datetime-local" value={wanneer}
                   onChange={(e) => setWanneer(e.target.value)} style={{ width: 205 }} />
          </label>
          <label style={{ display: 'grid', gap: 4, flex: 1, minWidth: 190 }}>
            <span className="mut2" style={{ fontSize: 12 }}>Note (optional)</span>
            <input className="inp" value={notitie}
                   onChange={(e) => setNotitie(e.target.value)} />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span className="mut2" style={{ fontSize: 12 }}>Attach proof</span>
            <input className="inp" type="file" accept="image/*,application/pdf"
                   onChange={(e) => setBewijs(e.target.files?.[0] ?? null)}
                   style={{ width: 210 }} />
          </label>
          <button className="btn sm" type="submit" disabled={bezig || !bedrag}>
            {bezig ? 'Submitting…' : 'Submit'}
          </button>
          {melding ? <span className="mut2" style={{ fontSize: 12 }}>{melding}</span> : null}
        </form>

        {(handmatig.data?.items ?? []).length > 0 ? (
          <div className="tblwrap">
          <table className="tbl" style={{ marginTop: 14 }}>
            <thead>
              <tr><th>Occurred</th><th>Note</th><th className="num">Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {(handmatig.data?.items ?? []).map((m) => (
                <tr key={m.id}>
                  <td className="mono">{datum(m.occurred_at)}</td>
                  <td>{m.note || '—'}</td>
                  <td className="num mono">{geld(m.amount)}</td>
                  <td>
                    <Tag kind={String(m.status) === 'approved' ? 'ok'
                             : String(m.status) === 'rejected' ? 'bad' : 'mut'}>
                      {m.status ?? 'pending'}
                    </Tag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : null}
      </Panel>

      <Panel inklap="mijn-earnings.verkopen" title="My Sales"
             desc="Open a row to view the conversation. Paid-message rows jump to the exact unlocked item."
             flush>
        {!d?.platform_sales?.length ? (
          <Empty>No platform sales in this period.</Empty>
        ) : (
          <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th><th>Type</th><th>Model</th><th>Fan</th><th>Platform</th>
                <th className="num">Gross</th><th className="num">My earnings</th>
              </tr>
            </thead>
            <tbody>
              {d.platform_sales.map((v) => {
                const mijn = v.counts_for_commission === false
                  ? 0
                  : Number(v.net ?? 0) * (Number(d.commission_pct) / 100)
                const heen = Number(v.conversation_id) > 0
                  ? `/conversations/show/${v.conversation_id}`
                    + (Number(v.message_id) > 0 ? `?message=${v.message_id}` : '')
                  : null
                return (
                  <tr key={v.revenue_event_id}
                      className={heen ? 'openrow' : undefined}
                      style={heen ? { cursor: 'pointer' } : undefined}
                      onClick={heen ? () => { window.location.href = heen } : undefined}>
                    <td className="mono">{tijd(v.occurred_at)}</td>
                    <td>{soort(v.event_type)}</td>
                    <td>{v.model_name ?? '—'}</td>
                    <td>{v.fan ?? '—'}</td>
                    <td>{String(v.platform) === 'f2f' ? 'F2F' : 'FanCentro'}</td>
                    <td className="num mono">{geld(v.amount)}</td>
                    <td className="num mono">{geld(mijn)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        )}
      </Panel>

      {manager ? (
        <Panel inklap="mijn-earnings.team" title="Team sales" flush
               desc="All sales in this brand, gross only. Subscriptions are not included. Sales without an assigned chatter are marked Unassigned. Your own sales are marked Yours: they are the same sales as in My Sales, not extra. Referral shows what a sale earned you: only sales by a chatter on your team, made while they were linked to you. The totals follow the filter."
               right={
                 // max-content: de knoppen blijven elk op één regel naast de uitleg.
                 // Telefoon: vijf knoppen passen niet op één regel, dus daar mogen ze
                 // afbreken (.seg wrapt onder 900px) in plaats van de pagina te verbreden.
                 <div style={mobiel ? undefined : { minWidth: 'max-content' }} data-testid="team-sales-filter">
                   <Seg options={TEAM_FILTERS} value={teamFilter}
                        onChange={(w) => setTeamFilter(w as TeamFilter)} />
                 </div>
               }>
          {team.error ? (
            <Empty>Team sales could not be loaded.</Empty>
          ) : !(team.data?.items ?? []).length ? (
            <Empty>{team.loading && !team.data ? 'Loading…' : 'No team sales in this period.'}</Empty>
          ) : (
            <>
            <div data-testid="team-sales-totaal" className="mut2"
                 style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '10px 14px', fontSize: 12.5 }}>
              <span><b className="num mono" data-testid="team-sales-aantal">{teamTotaal?.count ?? 0}</b> sales</span>
              <span>Gross <b className="num mono" data-testid="team-sales-gross">{geld(teamTotaal?.gross)}</b></span>
              <span>Your referral <b className="num mono" data-testid="team-sales-referral">{geld(teamTotaal?.referral)}</b></span>
            </div>
            {!teamRijen.length ? (
              <Empty>No sales for this filter in this period.</Empty>
            ) : mobiel ? (
              // Telefoon: een kaart per verkoop in plaats van acht kolommen.
              // De tooltip bestaat daar niet, dus de uitleg staat eronder.
              <div data-testid="team-sales" style={{ display: 'grid' }}>
                {teamRijen.map((v) => {
                  const b = referralBadge(v)
                  return (
                    <div key={v.revenue_event_id} data-testid="team-sales-rij"
                         style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)',
                                  display: 'grid', gap: 4 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <b>{v.chatter_name ?? (v.group === 'unassigned' ? 'Unassigned' : '—')}</b>
                        {v.group === 'yours' ? <Tag kind="acc">Yours</Tag> : null}
                        <span className="num mono" data-testid="team-sales-rij-gross"
                              style={{ marginLeft: 'auto' }}>{geld(v.gross)}</span>
                      </div>
                      <div className="mut2" style={{ fontSize: 12 }}>
                        {tijd(v.occurred_at)} · {soort(v.event_type)} · {v.model_name ?? '—'} · {v.fan ?? '—'} · {String(v.platform) === 'f2f' ? 'F2F' : 'FanCentro'}
                      </div>
                      <div>
                        <span className={`tag ${b.kind}`} title={b.uitleg} data-testid="referral-badge"
                              style={BADGE_STIJL}>{b.tekst}</span>
                      </div>
                      <div className="mut2" style={{ fontSize: 11.5 }}>{b.uitleg}</div>
                    </div>
                  )
                })}
              </div>
            ) : (
            <div className="tblwrap" data-testid="team-sales-wrap">
            <table className="tbl" data-testid="team-sales">
              <thead>
                <tr>
                  <th>When</th><th>Chatter</th><th>Type</th><th>Model</th><th>Fan</th>
                  <th>Platform</th><th className="num">Gross</th><th>Referral</th>
                </tr>
              </thead>
              <tbody>
                {teamRijen.map((v) => {
                  const b = referralBadge(v)
                  return (
                    <tr key={v.revenue_event_id} data-testid="team-sales-rij">
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>{tijd(v.occurred_at)}</td>
                      <td>
                        {v.chatter_name ?? (v.group === 'unassigned'
                          ? <span className="mut2" data-testid="team-sales-unassigned">Unassigned</span> : '—')}
                        {v.group === 'yours' ? <> <Tag kind="acc">Yours</Tag></> : null}
                      </td>
                      <td>{soort(v.event_type)}</td>
                      <td>{v.model_name ?? '—'}</td>
                      <td>{v.fan ?? '—'}</td>
                      <td>{String(v.platform) === 'f2f' ? 'F2F' : 'FanCentro'}</td>
                      <td className="num mono" data-testid="team-sales-rij-gross">{geld(v.gross)}</td>
                      <td style={BADGE_STIJL}>
                        <span className={`tag ${b.kind}`} title={b.uitleg} aria-label={`${b.tekst}. ${b.uitleg}`}
                              data-testid="referral-badge" style={BADGE_STIJL}>{b.tekst}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
            )}
            </>
          )}
        </Panel>
      ) : null}

      <Panel inklap="mijn-earnings.uitbetalingen" title="Payout history" flush
             right={<span className="mut2" style={{ fontSize: 12 }}>
               Total paid out <b className="num mono">
                 {geld(balans?.paid_commission ?? betaald)}</b></span>}>
        {!(uitbetalingen.data?.items ?? []).length ? (
          <Empty>No payouts yet.</Empty>
        ) : (
          <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Period</th><th className="num">Gross</th><th className="num">Commission</th>
                <th className="num">Paid</th><th>Status</th><th>Paid at</th><th>Note</th>
              </tr>
            </thead>
            <tbody>
              {(uitbetalingen.data?.items ?? []).map((u) => (
                <tr key={u.id}>
                  <td className="mono">{datum(u.period_start)} – {datum(u.period_end)}</td>
                  <td className="num mono">{geld(u.gross_amount)}</td>
                  <td className="num mono">{geld(u.commission_amount)}</td>
                  <td className="num mono">{geld(u.amount_paid)}</td>
                  <td>
                    <Tag kind={String(u.status).toLowerCase() === 'paid' ? 'ok' : 'mut'}>
                      {u.status ?? '—'}
                    </Tag>
                  </td>
                  <td className="mono">{u.paid_at ? datum(u.paid_at) : '—'}</td>
                  <td>{u.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Panel>
    </>
  )
}
